/**
 * Programs API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type { Program, CreateProgramDto, UpdateProgramDto } from '@/types/program.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Append query params to a path when the search string is non-empty. */
function listUrl(path: string, searchParams: URLSearchParams): string {
  const q = searchParams.toString();
  return q ? `${path}?${q}` : path;
}

/** Trigger a browser download of an XLSX blob. */
function downloadXlsx(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /academic/programs` — paginated programs. */
export const getPrograms = async (params?: {
  page?: number;
  pageSize?: number;
  statuses?: string[];
  types?: string[];
  coursePathTypes?: Array<'standard' | 'sport'>;
  search?: string;
}): Promise<PaginatedResponse<Program>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  params?.types?.forEach((t) => searchParams.append('types', t));
  params?.coursePathTypes?.forEach((t) => searchParams.append('coursePathTypes', t));
  if (params?.search) searchParams.append('search', params.search);
  const { data } = await apiClient.get<PaginatedResponse<Program>>(
    listUrl('/academic/programs', searchParams),
  );
  return data;
};

/** `GET /academic/programs/:id` */
export const getProgram = async (id: string): Promise<Program> => {
  const { data } = await apiClient.get(`/academic/programs/${id}`);
  return data;
};

/** `POST /academic/programs` */
export const createProgram = async (dto: CreateProgramDto): Promise<Program> => {
  const { data } = await apiClient.post('/academic/programs', dto);
  return data;
};

/** `PATCH /academic/programs/:id` */
export const updateProgram = async (id: string, dto: UpdateProgramDto): Promise<Program> => {
  const { data } = await apiClient.patch(`/academic/programs/${id}`, dto);
  return data;
};

/** `DELETE /academic/programs/:id` */
export const deleteProgram = async (id: string): Promise<void> => {
  await apiClient.delete(`/academic/programs/${id}`);
};

/** `GET /academic/programs/export` — download XLSX. */
export const exportProgramsExcel = async (): Promise<void> => {
  const response = await apiClient.get('/academic/programs/export', { responseType: 'blob' });
  downloadXlsx(response.data, `programs-${new Date().toISOString().slice(0, 10)}.xlsx`);
};
